import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Timer, Sparkles, Leaf, Award } from "lucide-react";
import { HERO_IMAGE, SERVICES, STRENGTHS } from "../data/content";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { MeasurementForm } from "../components/MeasurementForm";
import { Faq } from "../components/Faq";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STRENGTH_ICONS = { greitis: Timer, garantija: ShieldCheck, svara: Sparkles, sauga: Leaf };

export default function Home() {
  const heroRef = useRef(null);
  const location = useLocation();

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    if (location.hash === "#matavimas") {
      const t = setTimeout(() => {
        document.getElementById("matavimas")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [location]);

  return (
    <main data-testid="home-page">
      <Seo
        title="Įtempiamos lubos visoje Lietuvoje — ART Ceiling"
        description="Įtempiamos lubos su 10 metų garantija: matinės, blizgios, LED linijos, medžiaginės. Montavimas per 1 dieną. Nemokamas matavimas visoje Lietuvoje."
      />
      {/* HERO */}
      <section ref={heroRef} className="grain relative min-h-screen overflow-hidden bg-navy-950">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-52 -left-40 h-[30rem] w-[30rem] rounded-full bg-navy-400/20 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-32 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-12 lg:pt-40">
          <motion.div style={{ y: textY }}>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-sky-300"
            >
              <span className="h-px w-10 bg-sky-300/60" />
              Įtempiamos lubos · Visa Lietuva
            </motion.p>

            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.25}>Įtempiamos lubos.</MaskedLine>
              <MaskedLine delay={0.4} className="text-sky-300">
                Sumontuotos
              </MaskedLine>
              <MaskedLine delay={0.55}>per vieną dieną.</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg"
            >
              Prabangios įtempiamos lubos su 10 metų garantija. Švarus, tylus montavimas be dulkių — jūsų namai lieka nepriekaištingi.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => document.getElementById("matavimas")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-cta-measurement"
                className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-4 text-sm font-bold text-navy-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_16px_40px_-10px_rgba(56,189,248,0.45)]"
              >
                Nemokamas matavimas
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <Link
                to="/galerija"
                data-testid="hero-cta-gallery"
                className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:border-white/40 hover:bg-white/5"
              >
                Žiūrėti darbus
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-8 flex items-center gap-3 text-sm text-slate-400"
              data-testid="hero-experience-line"
            >
              <Award className="h-5 w-5 shrink-0 text-sky-300" />
              <span>
                <strong className="font-semibold text-white">7 metų patirtis</strong> ir šimtai patenkintų klientų visoje Lietuvoje
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.9 }}
              className="mt-10 grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-8"
              data-testid="hero-stats"
            >
              {[
                ["10 m.", "garantija"],
                ["1 d.", "montavimas"],
                ["100%", "švarus darbas"],
              ].map(([num, label]) => (
                <div key={label}>
                  <div className="font-heading text-2xl font-bold text-white sm:text-3xl">{num}</div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 1.5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
                <img
                  src={HERO_IMAGE.url}
                  alt={HERO_IMAGE.alt}
                  data-testid="hero-image"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 via-transparent to-transparent" />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.7 }}
                className="absolute -bottom-6 -left-4 rounded-2xl border border-white/10 bg-navy-900/90 px-6 py-4 backdrop-blur-xl sm:-left-8"
                data-testid="hero-warranty-badge"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-8 w-8 text-sky-300" />
                  <div>
                    <div className="font-heading text-lg font-bold text-white">10 metų garantija</div>
                    <div className="text-xs text-slate-400">medžiagoms ir darbams</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* STRENGTHS BENTO */}
      <section className="bg-white py-16 sm:py-24 lg:py-32" data-testid="strengths-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-600">Kodėl ART Ceiling</p>
            <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">
              Detalės, kurias pastebite kiekvieną dieną
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {STRENGTHS.map((s, i) => {
              const Icon = STRENGTH_ICONS[s.id];
              const wide = i === 0 || i === 3;
              return (
                <Reveal key={s.id} delay={i * 0.1} className={wide ? "md:col-span-7" : "md:col-span-5"}>
                  <div
                    data-testid={`strength-card-${s.id}`}
                    className="group relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-navy-900/20 hover:bg-white hover:shadow-[0_24px_50px_-20px_rgba(15,23,42,0.15)]"
                  >
                    <div className="flex items-start justify-between">
                      <Icon className="h-7 w-7 text-sky-600 transition-transform duration-500 group-hover:scale-110" />
                      <span className="font-heading text-5xl font-extrabold tracking-tight text-navy-900/10 transition-colors duration-500 group-hover:text-navy-900/20 sm:text-6xl">
                        {s.badge}
                      </span>
                    </div>
                    <h3 className="mt-8 font-heading text-xl font-semibold text-navy-900 sm:text-2xl">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-slate-50 py-16 sm:py-24 lg:py-32" data-testid="services-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-600">Paslaugos</p>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">
                Lubų sistemos kiekvienai erdvei
              </h2>
            </div>
            <Link
              to="/paslaugos"
              data-testid="services-preview-all-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-navy-900 transition-colors hover:text-sky-600"
            >
              Visos paslaugos
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link
                  to={`/paslaugos#${s.id}`}
                  data-testid={`service-card-${s.id}`}
                  className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-24px_rgba(15,23,42,0.25)]"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={s.image_url}
                      alt={s.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-lg font-semibold text-navy-900">{s.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-500">{s.subtitle}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-600">
                      Plačiau
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="grain relative overflow-hidden bg-navy-950 py-16 sm:py-24" data-testid="cta-band">
        <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-12">
          <Reveal>
            <h2 className="max-w-xl font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Tiksli kaina — tik po matavimo. Jis nemokamas.
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-400">
              Kiekviena erdvė skirtinga, todėl sąmatą sudarome tik apžiūrėję patalpą. Be įsipareigojimų, be paslėptų mokesčių.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              onClick={() => document.getElementById("matavimas")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="cta-band-measurement"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-8 py-4 text-sm font-bold text-navy-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_16px_40px_-10px_rgba(56,189,248,0.45)]"
            >
              Registruotis matavimui
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      <Faq />

      {/* MEASUREMENT FORM */}
      <section id="matavimas" className="bg-white py-16 sm:py-24 lg:py-32" data-testid="measurement-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-600">Nemokamas matavimas</p>
            <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">
              Registruokitės — atvyksime jums patogiu metu
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
              Užpildykite formą ir per 15 minučių susisieksime sutarti dėl matavimo laiko. Matavimas ir sąmata — visiškai nemokami, visoje Lietuvoje.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Tiksli sąmata vietoje — be paslėptų mokesčių",
                "Matavimas trunka vos 20–30 minučių",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-10">
              <MeasurementForm />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
